import {Pipe, PipeTransform} from '@angular/core';

/**
 * Translates technical BD enum values (charge actions, contract actions,
 * reference object types and message types) into German plain-text labels
 * for display in the dunning UI.
 *
 * Usage in templates:
 *   {{ charge.action | bdEnumLabel:'chargeAction' }}
 *   {{ contract.action | bdEnumLabel:'contractAction' }}
 *
 * Unknown values fall back to the raw value so nothing disappears silently.
 */
@Pipe({
    name: 'bdEnumLabel',
    standalone: false
})
export class BdEnumLabelPipe implements PipeTransform {

    // TM-1000001962: robust gegen BEIDE Backend-Serialisierungen des
    // BdDunningChargeAction-Enums — numerischer ORDINAL (Backend liefert
    // charge.action als Ordinal: 0=REMOVE, 1=REDUCE, 2=CONFIRM) UND
    // String-Enum-Name ('REMOVE'…). Ordinalreihenfolge gegen das Java-Enum
    // com.mvs.backend.bd.enums.BdDunningChargeAction verifiziert.
    private static readonly CHARGE_ACTION: { [key: string]: string } = {
        // String-Enum-Namen
        'REMOVE': 'Entfernen',
        'REDUCE': 'Reduzieren',
        'CONFIRM': 'Bestätigen',
        // Numerische Ordinals (Java-Enum-Deklarationsreihenfolge)
        '0': 'Entfernen',
        '1': 'Reduzieren',
        '2': 'Bestätigen'
    };

    private static readonly CONTRACT_ACTION: { [key: string]: string } = {
        'CANCEL': 'Kündigen',
        'REACTIVATE': 'Wieder in Kraft setzen',
        'HANDOVER': 'Inkasso-Übergabe',
        'WRITEOFF': 'Abschreiben',
        'RATE_AGREEMENT': 'Ratenzahlung',
        'LV_SB_TRANSFER': 'LV-SB-Übertrag',
        'SPECIAL_HANDLING': 'Sonderbehandlung',
        'CONTINUE_DUNNING': 'Mahnung fortsetzen'
    };

    private static readonly REFERENCE_OBJECT_TYPE: { [key: string]: string } = {
        'CHARGE': 'Gebühren',
        'DUNNING_CONTRACT': 'Verträge',
        'MANUAL_TRANSACTION': 'Manuelle Buchungen',
        'TRANSACTION': 'Transaktionen',
        'ONE_TIME_PAYMENT': 'Einmalzahlungen',
        // TM-1000001974: Storno-Ausgleich (Ausgleich Vertragsstornos) der Inkasso-Übergabe.
        'STORNO_AUSGLEICH': 'Ausgleich Vertragsstornos'
    };

    private static readonly MESSAGE_TYPE: { [key: string]: string } = {
        'MISSING': 'Fehlende Angabe',
        'WRONG': 'Fehlerhafte Angabe'
    };

    transform(
        // TM-1000001962: number zulassen — Backend kann Enums als Ordinal liefern
        value: string | number | null | undefined,
        kind: 'chargeAction' | 'contractAction' | 'referenceObjectType' | 'messageType'
    ): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        // TM-1000001962: Key normalisieren, damit numerischer Ordinal (0/1/2…)
        // wie der String-Enum-Name greift; unbekannte Werte fallen auf den
        // Rohwert zurück, damit nichts still verschwindet.
        const key = value.toString();
        const map = this.resolveMap(kind);
        return map[key] ?? key;
    }

    private resolveMap(kind: string): { [key: string]: string } {
        switch (kind) {
            case 'chargeAction':
                return BdEnumLabelPipe.CHARGE_ACTION;
            case 'contractAction':
                return BdEnumLabelPipe.CONTRACT_ACTION;
            case 'referenceObjectType':
                return BdEnumLabelPipe.REFERENCE_OBJECT_TYPE;
            case 'messageType':
                return BdEnumLabelPipe.MESSAGE_TYPE;
            default:
                return {};
        }
    }
}
